/**
 * Panneau upload / publication des assets d'une histoire (S-9c).
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import { getAssetUrl } from '../../utils/axios-config';
import {
  publishAdminStoryAsset,
  publishAllPendingAdminStoryAssets,
  uploadAdminStoryAsset,
} from '../../services/adminService';

const ASSET_KINDS = [
  { id: 'story_cover', label: 'Couverture de l’histoire', buildKey: (p) => `story_cover:${p.storyId}` },
  { id: 'biome_bg', label: 'Fond de biome', buildKey: (p) => `biome_bg:${p.biomeId}` },
  { id: 'map_bg', label: 'Fond carte monde', buildKey: (p) => `map_bg:${p.storyId}` },
  {
    id: 'zone',
    label: 'Calque de zone',
    buildKey: (p) => `zone:${p.zoneId}:${p.etat}`,
  },
  { id: 'story_ill', label: 'Illustration livre', buildKey: (p) => `story_ill:${p.illKey}` },
  { id: 'custom', label: 'Clé libre', buildKey: (p) => p.customKey },
];

const ZONE_ETATS = ['solid', 'placed', 'ruined'];

const STATUT_COLORS = {
  pending: 'warning',
  validated: 'info',
  published: 'success',
  archived: 'default',
};

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

function assetPreviewUrl(fileUrl) {
  if (!fileUrl) return null;
  const path = fileUrl.startsWith('/') ? fileUrl.slice(1) : fileUrl;
  return getAssetUrl(path.replace(/^assets\//, ''));
}

export default function AdventureStoryAssetsPanel({ storyId, biomes = [], onRefresh, onNotify }) {
  const fileInputRef = useRef(null);
  const [uploadKind, setUploadKind] = useState('biome_bg');
  const [biomeId, setBiomeId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [zoneEtat, setZoneEtat] = useState('solid');
  const [illKey, setIllKey] = useState('');
  const [customKey, setCustomKey] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [publishingAll, setPublishingAll] = useState(false);
  const [assets, setAssets] = useState([]);

  React.useEffect(() => {
    if (biomes.length > 0 && !biomeId) {
      setBiomeId(biomes[0].id);
    }
  }, [biomes, biomeId]);

  const resolvedAssetKey = useMemo(() => {
    const kind = ASSET_KINDS.find((k) => k.id === uploadKind);
    if (!kind) return '';
    return kind.buildKey({
      storyId,
      biomeId,
      zoneId,
      etat: zoneEtat,
      illKey,
      customKey: customKey.trim(),
    });
  }, [uploadKind, storyId, biomeId, zoneId, zoneEtat, illKey, customKey]);

  // `onRefresh` arrive en fonction fléchée, neuve à chaque rendu du parent : en
  // dépendre relançait le chargement, qui rafraîchit le parent, qui rend une
  // nouvelle fonction… une requête toutes les ~100 ms. On lit la dernière par
  // une ref, et seul le changement d'histoire recharge.
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const loadAssets = React.useCallback(async () => {
    if (!onRefreshRef.current) return;
    const list = await onRefreshRef.current();
    setAssets(list || []);
  }, []);

  React.useEffect(() => {
    loadAssets();
  }, [loadAssets, storyId]);

  const pendingCount = assets.filter((a) => a.statut === 'pending').length;

  const handleUpload = async () => {
    if (!selectedFile || !resolvedAssetKey) {
      onNotify?.('Fichier et clé requis', 'error');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      fd.append('assetKey', resolvedAssetKey);
      fd.append('assetType', selectedFile.type === 'application/json' ? 'layout_json' : 'image');
      await uploadAdminStoryAsset(storyId, fd);
      onNotify?.(`Asset « ${resolvedAssetKey} » téléversé (pending)`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadAssets();
    } catch (err) {
      onNotify?.(err.message || 'Erreur upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handlePublishOne = async (assetId) => {
    setPublishingId(assetId);
    try {
      await publishAdminStoryAsset(storyId, assetId);
      onNotify?.('Asset publié — clients notifiés au prochain sync');
      await loadAssets();
    } catch (err) {
      onNotify?.(err.message || 'Erreur publication', 'error');
    } finally {
      setPublishingId(null);
    }
  };

  const handlePublishAll = async () => {
    setPublishingAll(true);
    try {
      const res = await publishAllPendingAdminStoryAssets(storyId);
      onNotify?.(`${res?.count ?? 0} asset(s) publié(s)`);
      await loadAssets();
    } catch (err) {
      onNotify?.(err.message || 'Erreur publication', 'error');
    } finally {
      setPublishingAll(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Assets média
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Téléversez des webp/png → statut <strong>pending</strong>, puis publiez pour mettre à jour le manifeste client.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Nouvel upload
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Type d&apos;asset</InputLabel>
            <Select
              value={uploadKind}
              label="Type d'asset"
              onChange={(e) => setUploadKind(e.target.value)}
            >
              {ASSET_KINDS.map((k) => (
                <MenuItem key={k.id} value={k.id}>
                  {k.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {uploadKind === 'biome_bg' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Biome</InputLabel>
              <Select value={biomeId} label="Biome" onChange={(e) => setBiomeId(e.target.value)}>
                {biomes.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.nom} ({b.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {uploadKind === 'zone' && (
            <>
              <TextField
                size="small"
                label="ID zone (ex. foret_clairiere_noms)"
                value={zoneId}
                onChange={(e) => setZoneId(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>État</InputLabel>
                <Select value={zoneEtat} label="État" onChange={(e) => setZoneEtat(e.target.value)}>
                  {ZONE_ETATS.map((e) => (
                    <MenuItem key={e} value={e}>
                      {e}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {uploadKind === 'story_ill' && (
            <TextField
              size="small"
              label="Clé illustration (ex. foret_prologue)"
              value={illKey}
              onChange={(e) => setIllKey(e.target.value)}
              sx={{ minWidth: 260 }}
            />
          )}

          {uploadKind === 'custom' && (
            <TextField
              size="small"
              label="Clé complète (ex. biome_bg:foret_des_noms)"
              value={customKey}
              onChange={(e) => setCustomKey(e.target.value)}
              sx={{ minWidth: 320 }}
              fullWidth
            />
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Clé cible : <code>{resolvedAssetKey || '(incomplète)'}</code>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" component="label" size="small">
            Choisir un fichier
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept="image/webp,image/png,image/jpeg,application/json"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </Button>
          {selectedFile && (
            <Typography variant="body2">
              {selectedFile.name} ({formatBytes(selectedFile.size)})
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            disabled={uploading || !selectedFile || !resolvedAssetKey}
            onClick={handleUpload}
          >
            Téléverser
          </Button>
        </Box>
      </Paper>

      {pendingCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={publishingAll ? <CircularProgress size={14} /> : <PublishIcon />}
              disabled={publishingAll}
              onClick={handlePublishAll}
            >
              Publier tout ({pendingCount})
            </Button>
          }
        >
          {pendingCount} asset(s) en attente de publication
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Clé</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>v</TableCell>
              <TableCell>Taille</TableCell>
              <TableCell>Aperçu</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {row.assetKey}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.statut}
                    size="small"
                    color={STATUT_COLORS[row.statut] || 'default'}
                  />
                </TableCell>
                <TableCell>{row.version}</TableCell>
                <TableCell>{formatBytes(row.fileSize)}</TableCell>
                <TableCell>
                  {row.assetType === 'image' && row.fileUrl ? (
                    <Box
                      component="img"
                      src={assetPreviewUrl(row.fileUrl)}
                      alt=""
                      sx={{ height: 36, maxWidth: 80, objectFit: 'cover', borderRadius: 0.5 }}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell align="right">
                  {row.statut === 'pending' && (
                    <Button
                      size="small"
                      startIcon={
                        publishingId === row.id ? (
                          <CircularProgress size={14} />
                        ) : (
                          <PublishIcon />
                        )
                      }
                      disabled={publishingId === row.id}
                      onClick={() => handlePublishOne(row.id)}
                    >
                      Publier
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {assets.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Aucun asset en base pour cette histoire
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
